import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBar } from '@angular/material/snack-bar';
import {
  SnackBarConfig,
  SnackBarData,
} from 'src/app/services/snackbar/snackBar.service';
import resolveConfig from 'tailwindcss/resolveConfig';
import { theme } from '../../../../../tailwind.config.js';

export interface NotificationMessageConfig {
  textColour?: string;
  borderColour?: string;
  backgroundColour?: string;
}

const defaultNotificationMessageConfig: NotificationMessageConfig = {
  textColour: 'adminis-grey-500',
  backgroundColour: 'adminis-green-10',
  borderColour: 'adminis-green-500',
};

export const warningNotificationMessageConfig: NotificationMessageConfig = {
  ...defaultNotificationMessageConfig,
  backgroundColour: 'warning-orange-10',
  borderColour: 'warning-orange-500',
};

@Component({
  selector: 'notification-message',
  templateUrl: './notification-message.component.html',
})
export class NotificationMessageComponent implements OnInit {
  public theme = resolveConfig(theme);
  public snackBarConfig: SnackBarConfig;

  @Input() message: string;
  @Input() notificationMessageConfig: NotificationMessageConfig;
  @Input() warningMessage: boolean;

  constructor(
    @Optional()
    @Inject(MAT_SNACK_BAR_DATA)
    public data: SnackBarData,
    public snackBar?: MatSnackBar,
  ) {}

  /**
   * Configures the notification message settings.
   *
   * Overrides the inputted message with the snack bar message if it has been
   * launched from the snack bar service.
   *
   * Configures the notification message config with either the default config
   * or default warning config based on the warning message flag. Overrides the
   * default configs with any values passed in the inputted notification
   * message config.
   * If it has been launched from the snack bar service override the config
   * with any values passed in from the snack bar data.
   *
   * Sets the snackbar config only if it has been opened from the snack bar
   * service.
   */
  ngOnInit() {
    if (this.data?.message) {
      this.message = this.data.message;
    }

    this.notificationMessageConfig = this.warningMessage
      ? {
          ...warningNotificationMessageConfig,
          ...this.notificationMessageConfig,
        }
      : {
          ...defaultNotificationMessageConfig,
          ...this.notificationMessageConfig,
        };

    if (this.data?.notificationMessageConfig) {
      this.notificationMessageConfig = {
        ...this.notificationMessageConfig,
        ...this.data?.notificationMessageConfig,
      };
    }

    this.snackBarConfig = this.data?.snackBarConfig;
  }

  /**
   * Closes the snackbar.
   *
   * Note this should only be called from the template of this component and
   * only when is this been opened using the snack bar service.
   */
  public dismiss() {
    if (this.snackBar) {
      this.snackBar.dismiss();
    }
  }

  /**
   * Gets the specifed colour code from the tailwind config.
   * 
   * Assumes that the colour is in the format "colour-shade".
   * 
   * @param colour the colour to retreive.
   * @returns the colour code with !important.
   */
  public getColour(colour: string) {
    return (
      this.theme.extend.colors[colour.substring(0, colour.lastIndexOf('-'))][
        colour.substring(colour.lastIndexOf('-') + 1, colour.length)
      ] + ' !important'
    );
  }
}
